import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import User from "@insite/mobius/Icons/User";
import Menu from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LogOutFromPortal, ValidatAndAutoLogin } from "@rightstore/Services/AuthenticationService";
import getSignInUrl from "@rightstore/Utilities/getSignInUrl";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userName,
    firstName: state.context.session.firstName,
    lastName: state.context.session.lastName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    selectedVariant: getProductState(state, state.pages.productDetails.selectedProductId),
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    cheveronIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;

            > nav {
                padding: 20px 0;
            }
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
            padding: 10px 0;
        `,
    },
    titleIcon: {
        size: 22,
        color: "white",
    },
    cheveronIcon: {
        src: ChevronDown,
        size: 22,
        css: css`
            margin-left: 10px;
        `,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
            text-transform: uppercase;
            color: white;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    firstName,
    lastName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    selectedVariant,
}: Props) => {
    const showIcon = fields.visibilityState === "both" || fields.visibilityState === "icon";
    const showLabel = fields.visibilityState === "both" || fields.visibilityState === "label";

    const env = qs.parse(currentLocation.search, { ignoreQueryPrefix: true }).env

    const [signInUrl, setSignInUrl] = useState<string>("");

    if (fields.icon === "User") {
        icon = User;
    }

    useEffect(() => {
        const tryAutoLoginWithRightShipPlatformCookie = async () => {
            if (!userName || currentUserIsGuest || !isSignInPage) {
                const validateAndAutoLoginResult = await ValidatAndAutoLogin(env);
                if (validateAndAutoLoginResult && validateAndAutoLoginResult.loggedIn && validateAndAutoLoginResult.shouldReload) {
                    if (window) {
                        window.location.reload();
                    }
                }
            }
        }

        const tryGetAndSetSignInUrl = async () => {
            setSignInUrl(await getSignInUrl({ currentLocation, selectedVariant }));
        }

        tryAutoLoginWithRightShipPlatformCookie();

        tryGetAndSetSignInUrl();
    }, [currentLocation, selectedVariant]);

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const onSignOutHandler = async () => {
        (window as any).Intercom('shutdown');
        // Reset RightShip portal cookie                
        await LogOutFromPortal()
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const DashboardMenu = [
        { title: "Account Dashboard", url: "/myAccount" },
        { title: "Sign Out", onMenuItemClick: onSignOutHandler }
    ];

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            {fields.includeAccountMenu && userName && !currentUserIsGuest && (
                <Menu
                    descriptionId="accountMenu"
                    menuItems={DashboardMenu}
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    maxDepth={1}
                    menuTrigger={
                        <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
                            {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                            {showLabel && (
                                <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                    {firstName} {lastName}
                                </Typography>
                            )}
                            <Icon {...styles.cheveronIcon} />
                        </Clickable>
                    }
                    cssOverrides={{
                        wrapper: css`
                            flex: 0 1 auto;                          
                            
                            &:hover {
                                ul {
                                    width: 100%;
                                    min-width: 190px;
                                    border-radius: 6px;
                                    box-shadow: 0px 0px 20px rgba(0, 55, 69, 0.1);
                                    margin-top: 2px;                                    
                                    
                                    > li:hover {
                                        background: ${getColor("primary")};
                                    }

                                    > li:hover a{
                                        background: ${getColor("primary")};
                                    }

                                    > li:hover span {
                                        color: ${getColor("common.background")};
                                    }
                                }
                            }
                        `,
                    }}
                    data-test-selector="header_accountMenu"
                />
            )}
            {(!fields.includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                <Clickable
                    {...styles.titleClickable}
                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                    data-test-selector="header_signIn"
                >
                    {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                    {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                </Clickable>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
